import { PDFDocument, rgb } from 'pdf-lib';

import { fileRevision, sameFileRevision } from './fileRevision.js';
import { alignFacilityHeader } from './pdfFacility.js';
import { requireHolterReport } from './pdfReportType.js';
import {
  inspectHeartRateBox,
  inspectSummary,
  updateHeartRateBox,
  updateSummary,
} from './pdfSummary.js';
import { appendStripIndex, renumberReport } from './stripIndex.js';

export function matchesStudyPdf(name, studyId) {
  const match = /^(\d+)(?:[a-z][a-z0-9]*)?\.pdf$/i.exec(name);
  return Boolean(studyId && match && match[1] === String(studyId).trim());
}
export async function scanReportPdfs(directory) {
  const reports = [];
  for await (const [name, handle] of directory.entries()) {
    if (handle.kind === 'file' && /\.pdf$/i.test(name)) reports.push({ name, handle });
  }
  return reports.sort((a, b) => a.name.localeCompare(b.name));
}
let cachedReport = null; // Keep only one PDF in memory.
export async function readReport(report) {
  const file = await report.handle.getFile();
  if (file.size > 128 * 1024 * 1024) throw new Error('PDF exceeds the 128 MiB size limit.');
  if (cachedReport?.handle === report.handle && sameFileRevision(cachedReport.revision, file))
    return cachedReport.result;
  const bytes = await file.arrayBuffer();
  const pdf = await PDFDocument.load(bytes);
  requireHolterReport(pdf);
  let summary;
  try {
    const info = inspectSummary(pdf);
    summary = { recognized: info.recognized, min: info.min, max: info.max };
  } catch {
    summary = { recognized: false, min: null, max: null };
  }
  let heartRateBox;
  try {
    const info = inspectHeartRateBox(pdf);
    heartRateBox = { recognized: info.recognized, min: info.min, max: info.max };
  } catch {
    heartRateBox = { recognized: false, min: null, max: null };
  }
  const result = { bytes, pageCount: pdf.getPageCount(), summary, heartRateBox };
  cachedReport = { handle: report.handle, revision: fileRevision(file), result };
  return result;
}
export async function insertHrPage(
  reportBytes,
  hrBytes,
  pageNumber,
  summaryUpdate = null,
  alignBox = false
) {
  const report = await PDFDocument.load(reportBytes);
  requireHolterReport(report);
  if (!Number.isInteger(pageNumber) || pageNumber < 2 || pageNumber > report.getPageCount()) {
    throw new Error('Choose an insertion page between 2 and the report page count.');
  }
  // Footer geometry is the observed ARCS Letter report layout. Avoid covering
  // content on an unsupported page size or rotated report.
  for (const original of report.getPages()) {
    if (
      Math.abs(original.getWidth() - 612) > 0.5 ||
      Math.abs(original.getHeight() - 792) > 0.5 ||
      original.getRotation().angle !== 0
    ) {
      throw new Error('Footer insertion supports unrotated Letter-size ARCS reports only.');
    }
  }
  if (summaryUpdate) {
    await updateSummary(report, summaryUpdate);
    if (alignBox) await updateHeartRateBox(report, summaryUpdate);
  }
  const hr = await PDFDocument.load(hrBytes);
  if (hr.getPageCount() !== 1) throw new Error('The HR report must contain exactly one page.');
  await alignFacilityHeader(hr, report);
  const [page] = await report.copyPages(hr, [0]);
  // Embed the actual page-2 footer so its wording, date, version and fonts
  // are preserved without hard-coding patient/report-specific text.
  const source = await PDFDocument.load(reportBytes);
  const footer = await report.embedPage(source.getPage(1), {
    left: 0,
    bottom: 0,
    right: 612,
    top: 24,
  });
  page.drawRectangle({ x: 0, y: 0, width: 612, height: 24, color: rgb(1, 1, 1) });
  page.drawPage(footer, { x: 0, y: 0, width: 612, height: 24 });
  report.insertPage(pageNumber - 1, page);
  await appendStripIndex(report, pageNumber - 1, footer);
  await renumberReport(report);
  return report.save();
}
